import type { CombatRatings } from "@/game/model/character-stats";
import { getGameSettings } from "@/game/model/game-settings";

export const TOWER_MAX_FLOOR = 100;

export type TowerFloorDefinition = {
  floor: number;
  name: string;
  maxHp: number;
  xpReward: number;
  ratings: CombatRatings;
};

/** Repeat clears grant this fraction of base XP. */
export const TOWER_REPEAT_XP_FACTOR = 0.7;

export function towerFloorDefinition(floor: number): TowerFloorDefinition {
  const f = Math.min(TOWER_MAX_FLOOR, Math.max(1, Math.floor(floor)));
  const s = getGameSettings();
  const level = f;
  const hpScale = 1 + (f - 1) * (s.monsterHpPerLevelFactor + 0.06);
  const maxHp = Math.floor(s.monsterDefaultHp * hpScale * (1 + f * 0.04));
  const atkScale = 1 + level * s.monsterStatScalePerLevel;
  const baseAttack = Math.floor(s.monsterDefaultAttack * atkScale * (1 + f * 0.02));
  const baseDefense = Math.floor(
    s.monsterDefaultDefense * (1 + level * 0.025) * (1 + f * 0.015),
  );
  const baseDodge = Math.floor(
    s.monsterDefaultDodge * (1 + level * 0.02) + level * 0.3,
  );

  const ratings: CombatRatings = {
    level,
    attack: baseAttack,
    defense: baseDefense,
    dodge: baseDodge,
    hit: 40 + level * 0.5 + baseAttack * 0.2,
    critChance: Math.min(1, s.monsterDefaultCritRate + level * 0.001),
    critMultiplier: s.monsterDefaultCritMult + level * 0.002,
    attackSpeed: 0,
    moveSpeed: 0,
    hpRegenPerSec: 0,
    manaMaxBonus: 0,
  };

  const xpReward = Math.floor(40 + f * 18 + f * f * 0.55);

  return {
    floor: f,
    name: f % 10 === 0 ? `บอสชั้น ${f}` : `ชั้น ${f}`,
    maxHp,
    xpReward,
    ratings,
  };
}

export function towerXpForClear(
  floor: number,
  highestClearedBefore: number,
  baseXp?: number,
): { xp: number; firstClear: boolean } {
  const def = towerFloorDefinition(floor);
  const reward = baseXp ?? def.xpReward;
  const firstClear = floor > highestClearedBefore;
  const factor = firstClear ? 1 : TOWER_REPEAT_XP_FACTOR;
  return { xp: Math.floor(reward * factor), firstClear };
}
